import {
  DUE_BUCKETS,
  KEYWORD_MAX_LENGTH,
  NO_FACET_MATCH,
  WORKSPACE_CALENDAR_BOARD_LIMIT,
  ulidSchema,
  workspaceCalendarQuerySchema,
  workspaceCalendarSchema,
  type ApiError,
  type DueBucket,
  type WorkspaceCalendar,
} from '@molt/shared';
import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import { get } from './api';
import { parseCardTimestamp } from './card-dates';
import type { WeekStart } from './locale-formatting';
import { queryKeys } from './query-keys';

export { WORKSPACE_CALENDAR_BOARD_LIMIT };

export interface WorkspaceCalendarRequest {
  readonly workspaceId: string;
  readonly boardIds: readonly string[];
  readonly from: string;
  readonly to: string;
  readonly filter?: WorkspaceCalendarFilterSelection | undefined;
}

export interface WorkspaceCalendarFilterSelection {
  readonly members: readonly string[];
  readonly labels: readonly string[];
  readonly lists: readonly string[];
  readonly due: readonly DueBucket[];
  readonly keyword: string;
}

export interface WorkspaceCalendarMonth {
  readonly month: string;
  readonly label: string;
  readonly from: string;
  readonly to: string;
  readonly days: readonly WorkspaceCalendarDay[];
}

export interface WorkspaceCalendarDay {
  readonly key: string;
  readonly date: Date;
  readonly dayOfMonth: number;
  readonly inMonth: boolean;
  readonly today: boolean;
}

const MONTH_SEARCH_RE = /^(?<year>\d{4})-(?<month>0[1-9]|1[0-2])$/;

export const EMPTY_WORKSPACE_CALENDAR_FILTER: WorkspaceCalendarFilterSelection = {
  members: [],
  labels: [],
  lists: [],
  due: [],
  keyword: '',
};

export function workspaceCalendarQueryString(
  request: Omit<WorkspaceCalendarRequest, 'workspaceId'>,
): string {
  if (request.boardIds.length === 0) {
    const params = new URLSearchParams();
    params.set('from', request.from);
    params.set('to', request.to);
    return params.toString();
  }

  const filter = normaliseWorkspaceCalendarFilter(
    request.filter ?? EMPTY_WORKSPACE_CALENDAR_FILTER,
  );
  const parsed = workspaceCalendarQuerySchema.parse({
    boardIds: request.boardIds,
    from: request.from,
    to: request.to,
    ...(filter.members.length === 0 ? {} : { members: filter.members }),
    ...(filter.labels.length === 0 ? {} : { labels: filter.labels }),
    ...(filter.lists.length === 0 ? {} : { lists: filter.lists }),
    ...(filter.due.length === 0 ? {} : { due: filter.due }),
    ...(filter.keyword === '' ? {} : { keyword: filter.keyword }),
  });
  const params = new URLSearchParams();
  for (const boardId of parsed.boardIds) params.append('boardIds', boardId);
  params.set('from', parsed.from);
  params.set('to', parsed.to);
  for (const member of parsed.members ?? []) params.append('members', member);
  for (const label of parsed.labels ?? []) params.append('labels', label);
  for (const list of parsed.lists ?? []) params.append('lists', list);
  for (const due of parsed.due ?? []) params.append('due', due);
  if (parsed.keyword !== undefined) params.set('keyword', parsed.keyword);
  return params.toString();
}

export function getWorkspaceCalendar(
  request: WorkspaceCalendarRequest,
): Promise<WorkspaceCalendar> {
  return get(
    `/workspaces/${encodeURIComponent(request.workspaceId)}/calendar?${workspaceCalendarQueryString(request)}`,
    workspaceCalendarSchema,
  );
}

export function workspaceCalendarQueryOptions(
  request: WorkspaceCalendarRequest,
): UseQueryOptions<WorkspaceCalendar, ApiError> {
  const queryKey = workspaceCalendarQueryString(request);
  return {
    queryKey: queryKeys.workspaces.calendar(request.workspaceId, queryKey),
    queryFn: () => getWorkspaceCalendar(request),
  };
}

export function useWorkspaceCalendar(
  request: WorkspaceCalendarRequest,
  enabled: boolean,
): UseQueryResult<WorkspaceCalendar, ApiError> {
  return useQuery({ ...workspaceCalendarQueryOptions(request), enabled });
}

/**
 * `FR-NOTIF-032` -- the grid is aligned to the member's derived week start, so
 * the weekday header (ordered by the same value in `WorkspaceCalendarPage`)
 * always names the column it stands over. The default stays Sunday-first: it
 * preserves the grid every caller built before the derivation existed, and the
 * routes pass `weekStartForLocale(preferences.language)` explicitly.
 */
export function workspaceCalendarMonth(
  value: string | undefined,
  weekStartsOn: WeekStart = 0,
): WorkspaceCalendarMonth {
  const month = normaliseMonth(value);
  const match = MONTH_SEARCH_RE.exec(month);
  if (match?.groups === undefined) throw new Error(`Invalid month ${month}`);

  const year = Number(match.groups.year);
  const monthIndex = Number(match.groups.month) - 1;
  const start = new Date(year, monthIndex, 1);
  const end = new Date(year, monthIndex + 1, 1);
  const gridStart = new Date(start);
  gridStart.setDate(gridStart.getDate() - ((gridStart.getDay() - weekStartsOn + 7) % 7));
  const gridEnd = new Date(end);
  const trailing = (7 - ((gridEnd.getDay() - weekStartsOn + 7) % 7)) % 7;
  gridEnd.setDate(gridEnd.getDate() + trailing);

  const days: WorkspaceCalendarDay[] = [];
  const todayKey = dayKey(new Date());
  for (
    const cursor = new Date(gridStart);
    cursor < gridEnd;
    cursor.setUTCDate(cursor.getUTCDate() + 1)
  ) {
    const date = new Date(cursor);
    const key = dayKey(date);
    days.push({
      key,
      date,
      dayOfMonth: date.getDate(),
      inMonth: date.getMonth() === monthIndex,
      today: key === todayKey,
    });
  }

  return {
    month,
    label: new Intl.DateTimeFormat('en', {
      month: 'long',
      year: 'numeric',
    }).format(start),
    from: start.toISOString(),
    to: end.toISOString(),
    days,
  };
}

export function previousCalendarMonth(month: string): string {
  return shiftCalendarMonth(month, -1);
}

export function nextCalendarMonth(month: string): string {
  return shiftCalendarMonth(month, 1);
}

export function dayKey(value: Date | string): string {
  const date = typeof value === 'string' ? parseCardTimestamp(value) : value;
  if (date === null || Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export function selectedBoardIdsFromSearch(value: unknown): string[] {
  const raw = Array.isArray(value) ? value : value === undefined ? [] : [value];
  const selected: string[] = [];
  const seen = new Set<string>();

  for (const item of raw) {
    if (typeof item !== 'string' || item.trim() === '') continue;
    if (!ulidSchema.safeParse(item).success) continue;
    if (seen.has(item)) continue;
    seen.add(item);
    selected.push(item);
    if (selected.length === WORKSPACE_CALENDAR_BOARD_LIMIT) break;
  }

  return selected;
}

export function cappedBoardSelection(ids: readonly string[]): string[] {
  return ids.slice(0, WORKSPACE_CALENDAR_BOARD_LIMIT);
}

export function workspaceCalendarFilterFromSearch(
  search: string | URLSearchParams | Record<string, unknown>,
): WorkspaceCalendarFilterSelection {
  const params =
    search instanceof URLSearchParams || typeof search === 'string'
      ? new URLSearchParams(search)
      : searchParamsFromRecord(search);

  return normaliseWorkspaceCalendarFilter({
    members: params.getAll('members'),
    labels: params.getAll('labels'),
    lists: params.getAll('lists'),
    due: params.getAll('due') as DueBucket[],
    keyword: params.get('keyword') ?? '',
  });
}

export function workspaceCalendarFilterActiveCount(
  filter: WorkspaceCalendarFilterSelection,
): number {
  const normalised = normaliseWorkspaceCalendarFilter(filter);
  return (
    normalised.members.length +
    normalised.labels.length +
    normalised.lists.length +
    normalised.due.length +
    (normalised.keyword === '' ? 0 : 1)
  );
}

export function toggleWorkspaceCalendarFilterValue(
  filter: WorkspaceCalendarFilterSelection,
  facet: 'members' | 'labels' | 'lists' | 'due',
  value: string,
): WorkspaceCalendarFilterSelection {
  const current = new Set<string>(filter[facet]);
  if (current.has(value)) {
    current.delete(value);
  } else {
    current.add(value);
  }

  return normaliseWorkspaceCalendarFilter({
    ...filter,
    [facet]: [...current],
  });
}

export function setWorkspaceCalendarKeyword(
  filter: WorkspaceCalendarFilterSelection,
  keyword: string,
): WorkspaceCalendarFilterSelection {
  return normaliseWorkspaceCalendarFilter({ ...filter, keyword });
}

export function workspaceCalendarFilterSearchParams(
  filter: WorkspaceCalendarFilterSelection,
): URLSearchParams {
  const params = new URLSearchParams();
  const normalised = normaliseWorkspaceCalendarFilter(filter);
  for (const member of normalised.members) params.append('members', member);
  for (const label of normalised.labels) params.append('labels', label);
  for (const list of normalised.lists) params.append('lists', list);
  for (const due of normalised.due) params.append('due', due);
  if (normalised.keyword !== '') params.set('keyword', normalised.keyword);
  return params;
}

function normaliseMonth(value: string | undefined): string {
  if (value !== undefined && MONTH_SEARCH_RE.test(value)) return value;
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

function shiftCalendarMonth(month: string, delta: number): string {
  const match = MONTH_SEARCH_RE.exec(month);
  if (match?.groups === undefined) return normaliseMonth(undefined);
  const year = Number(match.groups.year);
  const monthIndex = Number(match.groups.month) - 1;
  const shifted = new Date(year, monthIndex + delta, 1);
  return `${shifted.getFullYear()}-${String(shifted.getMonth() + 1).padStart(2, '0')}`;
}

function searchParamsFromRecord(search: Record<string, unknown>): URLSearchParams {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(search)) {
    for (const item of arraySearchValue(value)) {
      params.append(key, item);
    }
  }
  return params;
}

function arraySearchValue(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string');
  }
  return typeof value === 'string' ? [value] : [];
}

function normaliseWorkspaceCalendarFilter(
  filter: WorkspaceCalendarFilterSelection,
): WorkspaceCalendarFilterSelection {
  return {
    members: normaliseFacet(filter.members, true),
    labels: normaliseFacet(filter.labels, true),
    lists: normaliseFacet(filter.lists, false),
    due: normalise(filter.due).filter(isDueBucket),
    keyword: normaliseKeyword(filter.keyword),
  };
}

function normaliseFacet(values: readonly string[], allowNone: boolean): string[] {
  return normalise(values).filter(
    (value) => value.length === 26 || (allowNone && value === NO_FACET_MATCH),
  );
}

function normalise(values: readonly string[]): string[] {
  return [...new Set(values.filter((value) => value !== ''))].sort();
}

function isDueBucket(value: string): value is DueBucket {
  return DUE_BUCKETS.includes(value as DueBucket);
}

function normaliseKeyword(value: string): string {
  return value.trim().slice(0, KEYWORD_MAX_LENGTH);
}
